import React, { useState, useMemo } from "react";
import { Search, MapPin, Phone, MessageCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { siteData } from "../mock";

const filters = ["All Properties", "Residential", "Commercial", "Industrial"];

function CardImage({ images, title }) {
  const [idx, setIdx] = useState(0);
  const go = (dir, e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => (i + dir + images.length) % images.length);
  };
  return (
    <div className="relative aspect-[4/3] overflow-hidden bg-[#f7f4ee]">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`${title} ${i + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      {images.length > 1 && (
        <>
          <button onClick={(e) => go(-1, e)} aria-label="Previous image" className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 hover:bg-white text-neutral-900 flex items-center justify-center shadow-sm">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={(e) => go(1, e)} aria-label="Next image" className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 hover:bg-white text-neutral-900 flex items-center justify-center shadow-sm">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {images.map((_, i) => (
              <span key={i} className={`h-[3px] transition-all ${i === idx ? "w-6 bg-white" : "w-3 bg-white/50"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Properties() {
  const [filter, setFilter] = useState("All Properties");
  const [query, setQuery] = useState("");
  const { whatsapp, primaryPhoneRaw } = siteData.brand;

  const visible = useMemo(() => {
    return siteData.properties.filter((p) => {
      const catOk = filter === "All Properties" || p.category === filter;
      const q = query.trim().toLowerCase();
      const textOk = !q || p.title.toLowerCase().includes(q) || p.location.toLowerCase().includes(q);
      return catOk && textOk;
    });
  }, [filter, query]);

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-[11px] tracking-[0.32em] text-[#b88b4a] mb-5">FEATURED PROPERTIES</div>
        <h2 className="font-serif text-neutral-900 text-[42px] leading-[1.05] md:text-[64px] md:leading-[1.03] tracking-tight max-w-4xl">
          Tricity&apos;s most wanted <span className="italic">addresses.</span>
        </h2>
        <p className="mt-6 text-neutral-700 text-[15.5px] md:text-[17px] leading-[1.8] max-w-3xl">
          Every listing below is physically inspected and legally vetted. Contact us for pricing, site visits and full documentation — no portal-style runaround.
        </p>

        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-5 py-2.5 text-[12.5px] tracking-wide border transition-colors ${filter === f ? "bg-jk-navy text-white border-jk-navy" : "bg-white text-neutral-800 border-neutral-300 hover:border-neutral-900"}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" strokeWidth={1.5} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by location, title..."
              className="w-full pl-11 pr-4 py-3 bg-white border border-neutral-300 text-[13.5px] focus:border-neutral-900 focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-6 text-[12px] tracking-wide text-neutral-500">
          Showing {visible.length} of {siteData.properties.length} listings
        </div>

        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {visible.map((p) => (
            <article key={p.id} className="border border-[#e7e2d7] bg-white group hover:border-[#c6a071] transition-colors flex flex-col">
              <CardImage images={p.images} title={p.title} />
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-[12.5px] text-neutral-500">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span>{p.location}</span>
                </div>
                <h3 className="mt-3 font-serif text-neutral-900 text-[24px] md:text-[26px] leading-[1.15]">{p.title}</h3>
                <div className="mt-2 text-[11.5px] tracking-[0.22em] text-[#b88b4a]">{p.tag}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="bg-jk-cream border border-[#e7e2d7] px-2.5 py-1 text-[10px] tracking-[0.2em] text-neutral-700">{p.category.toUpperCase()}</span>
                  {p.featured && <span className="bg-[#b88b4a] text-white px-2.5 py-1 text-[10px] tracking-[0.2em]">FEATURED</span>}
                  {p.exclusive && <span className="bg-jk-navy text-white px-2.5 py-1 text-[10px] tracking-[0.2em]">EXCLUSIVE</span>}
                </div>
                <p className="mt-4 text-neutral-700 text-[14.5px] leading-[1.75]">{p.desc}</p>
                <div className="mt-auto pt-6">
                  <div className="font-serif text-neutral-900 text-[20px]">{p.price}</div>
                  <div className="text-[11px] tracking-[0.25em] text-neutral-500 mt-1">REF · {p.id}</div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <a href={`tel:${primaryPhoneRaw}`} className="btn-dark inline-flex items-center justify-center gap-2 py-3 text-[12.5px] tracking-wide">
                    <Phone className="w-3.5 h-3.5" /> Call for Details
                  </a>
                  <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi JK Estates, I'm interested in "${p.title}" (Ref ${p.id}). Please share details.`)}`} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex items-center justify-center gap-2 py-3 text-[12.5px] tracking-wide">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp Now
                  </a>
                </div>
              </div>
            </article>
          ))}
          {visible.length === 0 && (
            <div className="md:col-span-2 lg:col-span-3 text-center py-16 text-neutral-500 text-[15px]">No listings match your filters. Try another category or clear your search.</div>
          )}
        </div>
      </div>
    </section>
  );
}
